/**
 * useCompositionTracking Hook
 *
 * Tracks IME (Input Method Editor) composition state globally to prevent
 * keyboard shortcuts from interfering with CJK (Chinese/Japanese/Korean) input.
 *
 * When a user types in Chinese, Japanese, or Korean, the IME enters "composition mode"
 * where characters are being composed but not yet finalized. During this time,
 * pressing keys like Escape should cancel the composition, NOT trigger shortcuts.
 *
 * This hook:
 * - Listens to compositionstart/compositionend events on the document
 * - Updates global ui$.isComposing state
 * - Uses capture phase to catch events early
 *
 * Usage:
 *   import { useCompositionTracking } from '../hooks/useCompositionTracking';
 *
 *   // In a component:
 *   useCompositionTracking();
 */

import { useEffect } from 'react';
import { setIsComposing } from '../store/ui$';

export const useCompositionTracking = () => {
  useEffect(() => {
    // Handle IME composition start
    const handleCompositionStart = () => {
      setIsComposing(true);
    };

    // Handle IME composition end
    const handleCompositionEnd = () => {
      setIsComposing(false);
    };

    // Use capture phase (true) to catch events early before they bubble
    // This ensures we catch composition events from any input element in the document
    document.addEventListener('compositionstart', handleCompositionStart, true);
    document.addEventListener('compositionend', handleCompositionEnd, true);

    return () => {
      // Cleanup: remove event listeners and reset state
      document.removeEventListener('compositionstart', handleCompositionStart, true);
      document.removeEventListener('compositionend', handleCompositionEnd, true);
      setIsComposing(false);
    };
  }, []); // Empty dependency array - only set up once
};
